import { LIB } from './library';
import type { BBox, Placed, Pt, Sym } from './types';

/** Sheet layout in mm (A3 landscape). */
export const SHEET = {
  GRID: 4,
  W: 420,
  H: 297,
  X0: 10,
  X1: 410,
  Y0: 8,
  Y1: 289,
  HDR: 6,
  COLS: 10,
  COLW: 40,
  TB_Y: 259,
} as const;

export const snapV = (v: number) => Math.round(v / SHEET.GRID) * SHEET.GRID;

export function rotP(x: number, y: number, r: number): Pt {
  r = ((r % 4) + 4) % 4;
  return r === 0 ? [x, y] : r === 1 ? [-y, x] : r === 2 ? [-x, -y] : [y, -x];
}

export function pinsW(s: Sym): Pt[] {
  return LIB[s.type].pins.map((p) => {
    const q = rotP(p[0], p[1], s.rot);
    return [s.x + q[0], s.y + q[1]];
  });
}

/** Path of a mechanical interlock (drawing-only symbol), from its origin. */
export function decoPts(s: Sym): Pt[] {
  const pts = (s.props.pts ?? '').trim().split(/\s+/).map((p) => p.split(',').map(Number)).filter((p) => p.length === 2 && p.every(Number.isFinite)) as Pt[];
  return pts.length >= 2 ? pts.slice(0, 20) : [[0, 0], [24, 0]];
}

export function bbLocal(s: Sym): BBox {
  const L = LIB[s.type];
  if (s.type === 'mint') {
    // the path, the triangle (IEC) under it and the note (NFPA) over it
    const p = decoPts(s), xs = p.map((q) => q[0]), ys = p.map((q) => q[1]);
    return [Math.min(...xs) - 1, Math.min(...ys) - 3, Math.max(...xs) + 1, Math.max(...ys) + 2.6];
  }
  if (L.role === 'text') {
    const z = Number(s.props.size) || 3.5;
    return [0, -z, Math.max(4, (s.props.text || '').length * z * 0.56), z * 0.3];
  }
  if (L.role === 'cable') return [0, -1.5, cableLen(s), 1.5];
  if (L.role === 'box') return [0, 0, boxW(s), boxH(s)];
  return L.bb;
}

export const boxW = (s: Sym) => Math.max(8, Math.min(400, Number(s.props.w) || 120));
export const boxH = (s: Sym) => Math.max(8, Math.min(280, Number(s.props.h) || 80));

export const cableLen = (s: Sym) => Math.max(8, Number(s.props.len) || 24);

/** World end points of a cable definition line. */
export function cableSegment(s: Sym): [Pt, Pt] {
  const e = rotP(cableLen(s), 0, s.rot);
  return [[s.x, s.y], [s.x + e[0], s.y + e[1]]];
}

export function bbWorld(s: Sym): BBox {
  const b = bbLocal(s);
  const c = [
    [b[0], b[1]],
    [b[2], b[1]],
    [b[0], b[3]],
    [b[2], b[3]],
  ].map((p) => rotP(p[0], p[1], s.rot));
  const xs = c.map((p) => p[0]);
  const ys = c.map((p) => p[1]);
  return [s.x + Math.min(...xs), s.y + Math.min(...ys), s.x + Math.max(...xs), s.y + Math.max(...ys)];
}

export const colOf = (x: number) =>
  Math.max(1, Math.min(SHEET.COLS, Math.floor((x - SHEET.X0) / SHEET.COLW) + 1));

/** Page.column location in EPLAN style, e.g. "2.4". */
export const loc = (it: Placed) => `${it.pi + 1}.${colOf(it.s.x)}`;

export const kxy = (x: number, y: number) => Math.round(x * 100) + ',' + Math.round(y * 100);

/** True when (x,y) lies strictly inside an orthogonal segment a–b. */
export function onSeg(x: number, y: number, a: Pt, b: Pt): boolean {
  const e = 1e-6;
  if (Math.abs(a[0] - b[0]) < e)
    return Math.abs(x - a[0]) < e && y > Math.min(a[1], b[1]) + e && y < Math.max(a[1], b[1]) - e;
  if (Math.abs(a[1] - b[1]) < e)
    return Math.abs(y - a[1]) < e && x > Math.min(a[0], b[0]) + e && x < Math.max(a[0], b[0]) - e;
  return false;
}

/** Removes duplicate and collinear points from a polyline. */
export function cleanPolyline(pts: Pt[]): Pt[] {
  const r: Pt[] = [];
  for (const p of pts) {
    const q: Pt = [+p[0], +p[1]];
    const l = r[r.length - 1];
    if (l && l[0] === q[0] && l[1] === q[1]) continue;
    r.push(q);
  }
  for (let changed = true; changed; ) {
    changed = false;
    for (let i = 1; i < r.length - 1; i++) {
      const a = r[i - 1], b = r[i], c = r[i + 1];
      if ((a[0] === b[0] && b[0] === c[0]) || (a[1] === b[1] && b[1] === c[1])) {
        r.splice(i, 1);
        changed = true;
        break;
      }
    }
  }
  return r;
}

/** Orthogonal route from a to b: straight if aligned, else one elbow. */
export function route(a: Pt, b: Pt, flip = false): Pt[] {
  if (a[0] === b[0] || a[1] === b[1]) return [b];
  let vertFirst = Math.abs(b[1] - a[1]) >= Math.abs(b[0] - a[0]);
  if (flip) vertFirst = !vertFirst;
  return vertFirst ? [[a[0], b[1]], b] : [[b[0], a[1]], b];
}
